import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, Map, Building2, Compass, 
  CheckCircle, ArrowRight, Loader2 
} from 'lucide-react';
import { LazyImage } from '@/components/ui/lazy-image';
import { ChinaBreadcrumbs } from '@/components/china/ChinaBreadcrumbs';
import { EmailCaptureCTA } from '@/components/china/EmailCaptureCTA';
import { useChinaContent } from '@/hooks/china/useChinaContent';

const featureCards = [
  {
    title: 'Planning',
    description: 'Visas, apps, payments, and everything you need before you go',
    icon: BookOpen,
    path: '/china/planning',
    color: 'bg-blue-500/10 text-blue-600',
  },
  {
    title: 'Itineraries',
    description: 'Day-by-day travel plans for all trip lengths',
    icon: Map,
    path: '/china/itineraries',
    color: 'bg-green-500/10 text-green-600',
  },
  {
    title: 'Cities & Regions',
    description: 'In-depth guides to Shanghai, Beijing, Xi\'an, and more',
    icon: Building2,
    path: '/china/cities',
    color: 'bg-purple-500/10 text-purple-600',
  },
  {
    title: 'Resources',
    description: 'Tools, apps, and practical travel resources',
    icon: Compass,
    path: '/china/resources',
    color: 'bg-orange-500/10 text-orange-600',
  },
];

const startHereChecklist = [
  'Set up Alipay or WeChat Pay before arrival',
  'Download offline maps (Google Maps won\'t work)',
  'Get a China SIM card or eSIM with data',
  'Book high-speed rail tickets on Trip.com or 12306',
  'Learn basic Mandarin phrases (or use translation apps)',
  'Understand visa requirements for your nationality',
  'Pack a VPN if you need access to Western apps',
  'Bring cash for small vendors (not everywhere takes mobile pay)',
];

export default function ChinaHub() {
  const { posts, loading } = useChinaContent({ limit: 6 });

  return (
    <>
      <Helmet>
        <title>China Travel Guide | Expert Tips & Itineraries | Trotting the World</title>
        <meta 
          name="description" 
          content="Your complete guide to traveling China. Expert planning advice, detailed itineraries, city guides, and essential resources for an unforgettable trip."
        />
        <meta property="og:title" content="China Travel Guide | Trotting the World" />
        <meta property="og:description" content="Expert travel guides, itineraries, and tips for exploring China." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/china`} />
      </Helmet>

      <div className="container py-8 md:py-12">
        <ChinaBreadcrumbs items={[]} />

        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="mb-4" variant="secondary">Destination Guide</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Explore China
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            For travellers who want the next step beyond Japan. 
            Ancient history, modern megacities, and landscapes you won't find anywhere else.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {featureCards.map((card) => (
            <Link key={card.path} to={card.path}>
              <Card className="h-full hover:shadow-lg transition-shadow group cursor-pointer">
                <CardContent className="p-6">
                  <div className={`p-3 rounded-lg inline-block mb-4 ${card.color}`}>
                    <card.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {card.description}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Start Here Checklist */}
        <Card className="mb-12">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold mb-6">Start Here: China Travel Checklist</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {startHereChecklist.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Email Capture */}
        <div className="mb-12">
          <EmailCaptureCTA 
            headline="Get the Complete China Travel Kit"
            description="Free guides, packing lists, and insider tips delivered to your inbox."
            source="china-hub"
          />
        </div>

        {/* Featured Content Grid */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Latest China Guides</h2>
            <Button variant="outline" asChild>
              <Link to="/blog?tag=China">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <Card key={post.id} className="overflow-hidden group hover:shadow-md transition-shadow">
                  <div className="aspect-video relative overflow-hidden">
                    <LazyImage
                      src={post.featured_image || 'https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b'}
                      alt={post.title}
                      className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-4">
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {post.tags.slice(0, 2).map(tag => (
                          <Badge key={tag.id} variant="secondary" className="text-xs">
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <h3 className="font-semibold line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {post.excerpt}
                      </p>
                    )}
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center mt-3 text-sm text-primary hover:underline"
                    >
                      Read more <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">
                  China content coming soon! Tag your posts with "China" to see them here.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
